/**
 * MSW request handler factory.
 *
 * Creates MSW handlers that intercept API requests and return fixture data.
 *
 * ## How it works
 *
 * A single catch-all handler intercepts any request to /apollo/*.
 * It derives the fixture filename from the URL path:
 *   /apollo/shipments/get-active-shipments → shipments/get-active-shipments.json
 *
 * Variants (cycling): if get-shipment-by-id1.json, get-shipment-by-id2.json exist,
 * they're cycled through via the FixtureSwitcher component.
 *
 * ## Browser vs Test environments
 *
 * The resolveFixture function is injected by the caller:
 * - Browser (worker.ts): fetches from Vite dev server on demand
 * - Tests (vitest.snapshots.setup.ts): reads from disk via readFileSync
 *
 * ## Fixture cycling
 *
 * Some endpoints have multiple fixture files (e.g., get-shipment-by-id1.json,
 * get-shipment-by-id2.json). The FixtureSwitcher component calls cycleFixture()
 * to rotate through them.
 */

import { http, HttpResponse, type JsonBodyType } from "msw";
import { type FixtureEndpoint } from "./endpoints";

// Track current fixture index per endpoint (for cycling between variants)
const fixtureIndex = new Map<string, number>();

function getCurrentFixtureIndex(path: string): number {
  const stored = fixtureIndex.get(path);
  if (stored !== undefined) return stored;
  // Read from URL param / localStorage (set by navigateFixture on page reload)
  const urlVal =
    new URLSearchParams(window.location.search).get("fixture") ?? undefined;
  if (urlVal !== undefined) return Number(urlVal);
  const page = window.location.pathname;
  const lsVal = localStorage.getItem(`fixture:${page}`) ?? undefined;
  if (lsVal !== undefined) return Number(lsVal);
  return 0;
}

/**
 * Create MSW handlers from a list of fixture endpoints.
 *
 * Two modes:
 * - **Parsed** (browser): resolveFixture returns a parsed object, or a promise
 *   of one — the browser fetches each fixture the first time it is requested
 *   → HttpResponse.json(data)
 * - **Raw** (tests): resolveFixture returns a raw JSON string → HttpResponse(string)
 *   Skips JSON.parse + JSON.stringify for better test performance.
 */
export function createHandlers(
  resolveFixture: (filename: string) => string | undefined,
  options: { raw: true },
  endpoints: FixtureEndpoint[],
): ReturnType<typeof http.all>[];
export function createHandlers(
  resolveFixture: (filename: string) => unknown,
  options: { raw?: false } | undefined,
  endpoints: FixtureEndpoint[],
): ReturnType<typeof http.all>[];
export function createHandlers(
  resolveFixture: (filename: string) => string | unknown,
  { raw = false }: { raw?: boolean } = {},
  endpoints: FixtureEndpoint[],
): ReturnType<typeof http.all>[] {
  return endpoints.map((endpoint) => {
    return http.all(`*${endpoint.path}`, async () => {
      const index = getCurrentFixtureIndex(endpoint.path);
      const filename = endpoint.fixtures[index] ?? endpoint.fixtures[0]!;
      const data = await resolveFixture(filename);
      if (!data) {
        return HttpResponse.json(
          { error: `No fixture data for ${filename}` },
          { status: 404 },
        );
      }
      if (raw) {
        return new HttpResponse(data as BodyInit, {
          headers: { "Content-Type": "application/json" },
        });
      }
      return HttpResponse.json(data);
    });
  });
}

/**
 * Create MSW handlers for inline (non-fixture) API responses.
 * Used for endpoints like thumbnail downloads that always return the same response.
 */
export function createInlineHandlers(
  responses: Record<string, unknown>,
): ReturnType<typeof http.all>[] {
  return Object.entries(responses).map(([path, data]) =>
    http.all(`*${path}`, () => HttpResponse.json(data as JsonBodyType)),
  );
}
